import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react";
import { createClient, type Session } from "@supabase/supabase-js";
import { apiFetch } from "../lib/api";
import { comPrazo } from "../lib/erroSessao";
import { definirPapelAtual } from "../lib/papelAtual";
import { precisaRenovar, expiraEmMs, ligarRenovacaoPeloUso } from "../lib/sessaoViva";
import {
  classeDaSaida,
  consumirMotivoDaQueda,
  enviarQuedasPendentes,
  limparSessaoViva,
  marcarMotivoDaQueda,
  marcarSessaoViva,
  motivoDaQuedaNoBoot,
  registrarQueda,
  sessaoGuardada,
  sessaoVivaDesde,
} from "../lib/quedaDeSessao";

/**
 * OS DOIS PRAZOS DO BOOT.
 *
 * `PRAZO_PERFIL_BORDA_MS` — o quanto o painel espera pelo /api antes de tentar o
 * caminho direto ao Supabase. Folgado em relação ao medido em produção (120–360ms
 * pelo /api), curto o bastante pra sobrar tempo pro caminho de segurança.
 *
 * `TETO_PERFIL_MS` — o teto dos DOIS caminhos somados. É o compromisso que a tela
 * faz com quem está olhando pra ela: em no máximo este tempo o painel decide
 * alguma coisa. Não é "quanto o servidor demora", é "quanto eu deixo o dono
 * olhando pra um spinner sem resposta".
 */
const PRAZO_PERFIL_BORDA_MS = 6_000;
export const TETO_PERFIL_MS = 12_000;

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

/** A linha de `users` que o painel precisa — mesma seleção nos dois caminhos. */
interface PerfilRow {
  nome?: string;
  role?: "admin" | "encarregado" | "cliente";
  cliente_id?: string;
  is_owner?: boolean;
}

interface AuthUser {
  id: string;
  email: string;
  nome?: string;
  role?: "admin" | "encarregado" | "cliente";
  cliente_id?: string;
  is_owner?: boolean; // admin master (gerencia usuários, config, exclusões)
}

interface AuthContextValue {
  user: AuthUser | null;
  session: Session | null;
  /**
   * O auth ainda está trabalhando. NÃO use isto pra decidir se manda alguém pro
   * login — use `bootResolvido`. Ver a diferença logo abaixo.
   */
  loading: boolean;
  /**
   * 🔴 O TERCEIRO ESTADO — e a razão dele existir.
   *
   * `user === null` significava DUAS coisas: "não há usuário" e "ainda não sei se
   * há usuário". O `PrivateRoute` tratava as duas como a primeira e expulsava
   * pro /login. Medido pelo Ruddy em produção (15/08): token válido por mais uma
   * hora, tela de Login na cara dele, e o painel carregando sozinho segundos
   * depois. Quem estava digitando lia isso como "caí" — e perdia o que digitou.
   *
   * `bootResolvido` responde só uma pergunta: **já dá pra dizer se há ou não
   * usuário?** Ele vira true em três situações, e em nenhuma outra:
   *
   *   1. um perfil resolveu (`user` preenchido) — o caminho normal;
   *   2. a queda foi CONFIRMADA: não há token válido no armazenamento (o mesmo
   *      juiz do `classeDaSaida`, que já estava no ar e testado);
   *   3. o teto estourou — e é o `TETO_PERFIL_MS` que JÁ EXISTE, não um número
   *      novo. Espera do mesmo tipo não ganha segundo relógio.
   *
   * A regra 3 é a trava contra a cicatriz de 28/07 (`/me/perfil` PENDENTE
   * deixando o painel em "Carregando…" pra sempre): no pior caso a pessoa espera
   * o teto e VAI pro login. Segurar mais que isso seria trocar um defeito por
   * outro pior.
   */
  bootResolvido: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  getAccessToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootResolvido, setBootResolvido] = useState(false);
  // Ref pra cancelar loadUserProfile em voo quando a sessão é zerada
  // (evita race: onAuthStateChange zera sessão, mas loadUserProfile ainda
  // estava em andamento e sobrescrevia user com dados parciais).
  const activeSessionRef = useRef<string | null>(null);
  // TRÊS CHAMADAS IDÊNTICAS VIRAM UMA. `getSession().then` e o
  // `onAuthStateChange` (INITIAL_SESSION + SIGNED_IN) chamavam loadUserProfile
  // em sequência com a MESMA sessão — medido em produção 27/07: 3 (às vezes 4)
  // consultas iguais ao perfil, todas no caminho crítico do boot. Guardo a
  // PROMESSA (não só um sinalizador) pra quem chegar depois esperar o mesmo
  // trabalho — o `await loadUserProfile` do signIn continua valendo, e a corrida
  // do "só entra na 2ª tentativa" não volta.
  const perfilEmVooRef = useRef<{ token: string; promessa: Promise<void> } | null>(null);

  /**
   * AS DUAS COISAS QUE SEPARAM "CAIU" DE "SAIU".
   *
   * `saidaPedidaRef` — o clique em "Sair" existiu? Quem pediu pra sair não
   * sofreu defeito nenhum, e contar isso como queda transformaria o instrumento
   * num contador de uso normal do painel.
   *
   * `tinhaSessaoRef` — havia sessão pra perder? `onAuthStateChange` dispara
   * `INITIAL_SESSION` com `null` em toda aba que abre no /login sem ninguém
   * logado. Sem esta guarda, "abrir o painel deslogado" viraria "queda de
   * sessão" e o número novo nasceria mentindo — que é pior do que não ter
   * número nenhum.
   */
  const saidaPedidaRef = useRef(false);
  const tinhaSessaoRef = useRef(false);
  /**
   * 🔴 O BOOT PODE PERGUNTAR DUAS VEZES — E A QUEDA É UMA SÓ.
   *
   * Medido ao vivo em 14/08: o registro saiu DUPLICADO, dois carimbos com 9ms de
   * diferença. É o StrictMode do React remontando o efeito em desenvolvimento —
   * mesmo instante, mesmo acontecimento, dois `getSession()`. Instrumento que
   * conta o mesmo fato duas vezes é pior que instrumento nenhum: ele produz um
   * número que parece medição.
   *
   * O `ref` sobrevive à remontagem do efeito na MESMA instância, que é
   * exatamente o recorte certo: uma carga de página tem um boot só. A queda em
   * pleno uso (o `SIGNED_OUT` lá embaixo) NÃO passa por aqui — ela pode
   * acontecer depois, num painel que já entrou, e tem que contar.
   */
  const quedaDeBootRef = useRef(false);

  useEffect(() => {
    // ANTES de perguntar: havia token guardado neste navegador? Lido AGORA,
    // síncrono, porque o supabase-js apaga a chave dele quando o refresh falha —
    // um instante depois a resposta já seria "não". Ver `lib/quedaDeSessao`.
    const guardada = sessaoGuardada();
    // O MARCO PRÓPRIO, lido junto. Ele é o que faz a deteção funcionar quando o
    // supabase-js já apagou a chave dele antes do React montar — foi assim que a
    // queda de 15/08, em produção, passou batida pela primeira versão disto.
    const vivaDesde = sessaoVivaDesde();
    // 🔴 E A ROTA TAMBÉM É LIDA AGORA. Ao vivo, o registro saiu com `/login`
    // enquanto a pessoa estava em `/orcamentos`: quando o `.then` roda, o painel
    // JÁ redirecionou. A tela em que a pessoa estava é metade do valor do
    // registro — é ela que diz o que se perdeu.
    const rotaNoBoot = window.location.pathname || "/";
    /**
     * A TRAVA CONTRA A CICATRIZ DE 28/07.
     *
     * Segurar a tela em "Carregando…" é o conserto — mas segurar PARA SEMPRE é o
     * defeito que já aconteceu (`/me/perfil` pendurado, painel morto). Passado o
     * teto, o boot se declara resolvido de qualquer jeito: sem usuário, a pessoa
     * vai pro login, exatamente como hoje.
     *
     * É o MESMO `TETO_PERFIL_MS` do perfil, de propósito: os dois medem a mesma
     * coisa — quanto tempo eu deixo alguém olhando pro spinner. Dois números pro
     * mesmo tipo de espera é como se cria a divergência que ninguém percebe.
     */
    const tetoDoBoot = window.setTimeout(() => setBootResolvido(true), TETO_PERFIL_MS);
    /** uma queda por carga de página (ver `quedaDeBootRef`). */
    const registrarQuedaDoBoot = (motivo: string, classe: "queda" | "flash_login" = "queda") => {
      if (quedaDeBootRef.current) return;
      quedaDeBootRef.current = true;
      registrarQueda({ classe, quando: new Date().toISOString(), motivo, rota: rotaNoBoot });
    };
    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        setSession(data.session);
        if (data.session?.user) {
          tinhaSessaoRef.current = true;
          marcarSessaoViva();
          loadUserProfile(data.session);
        } else {
          // 🔴 O CAMINHO INVISÍVEL — e ele NÃO passa pelo `.catch` abaixo.
          //
          // Medido ao vivo em 14/08, com um token vencido plantado no navegador:
          // `getSession()` não lança. Devolve `{ session: null, error: "Refresh
          // token is not valid" }`, apaga a chave e o painel cai no login. Nenhuma
          // requisição sai, nenhum status acontece, `app_errors` fica com ZERO 401
          // — e o dono, deslogado. A primeira versão deste registro morava só no
          // `.catch` e não pegou NADA nesse teste.
          //
          // Havia token e não há sessão = alguém foi deslogado. É isso que se
          // registra. Sem token guardado, é só uma aba abrindo no /login: rotina,
          // e registrar rotina faria o número novo nascer mentindo.
          // `error` costuma vir VAZIO aqui (o auto-refresh do supabase-js já
          // limpou a chave antes de perguntarmos) — por isso o motivo é montado
          // com o que dava pra saber no instante certo. Ver `motivoDaQuedaNoBoot`.
          if (guardada.havia || vivaDesde) {
            // Mesma separação da outra porta: token ainda válido = corrida do
            // boot, não sessão perdida.
            const flash = guardada.havia && guardada.vencidaHaS !== null && guardada.vencidaHaS < 0;
            registrarQuedaDoBoot(
              flash
                ? "boot terminou sem sessão com o token AINDA VÁLIDO — corrida do boot"
                : motivoDaQuedaNoBoot(guardada, error?.message, vivaDesde),
              flash ? "flash_login" : "queda",
            );
            if (!flash) limparSessaoViva(); // a queda foi contada; o marco cumpriu a função
            // 🔑 SÓ A QUEDA CONFIRMADA RESOLVE O BOOT. No `flash` o token ainda
            // vale: dizer "não há usuário" agora é a mentira que expulsava o dono
            // do painel. Segura, e quem resolve é o perfil que está a caminho —
            // ou o teto.
            if (!flash) setBootResolvido(true);
          } else {
            // Nem token, nem marco: ninguém estava logado. Aba abrindo no /login
            // é rotina, e o boot está resolvido — não há o que esperar.
            setBootResolvido(true);
          }
          setLoading(false);
        }
      })
      .catch(async (e) => {
        // Token velho/corrompido no storage (ex.: "Invalid Refresh Token") NÃO
        // pode sequestrar o painel em "Carregando…" pra sempre. Limpa a sessão
        // ruim e cai pro login — auto-recuperação, sem depender de limpar cache
        // na mão.
        console.warn("[auth] getSession falhou, limpando sessão:", e?.message);
        // O MESMO registro do ramo de cima, pro caso em que `getSession` de fato
        // rejeita. Medido ao vivo, o refresh inválido NÃO passa por aqui (ver o
        // comentário acima) — mas este ramo já existia antes de mim, e um dia
        // alguma falha vai cair nele. Sem `haviaToken`: se a promessa rejeitou,
        // havia o que ler.
        registrarQuedaDoBoot(`getSession falhou: ${e?.message ?? "sem mensagem"}`);
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* já era */ }
        setSession(null);
        setUser(null);
        setLoading(false);
        // A promessa rejeitou: não há como haver usuário. Boot resolvido.
        setBootResolvido(true);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      if (sess?.user) {
        tinhaSessaoRef.current = true;
        marcarSessaoViva();
        loadUserProfile(sess);
      } else {
        // A OUTRA PORTA: a sessão sumiu sem ninguém pedir. Acontece quando o
        // refresh do Supabase falha em pleno uso, e também quando o `onError` do
        // React Query desloga por 401 (`main.tsx`) — que marca o motivo antes,
        // senão a causa chegaria aqui como "desconhecido".
        // 🔴 AQUI MORAM DUAS COISAS DIFERENTES COM A MESMA CARA — medido em
        // produção, 15/08: o painel foi pro /login com o token VÁLIDO por mais
        // uma hora, e carregou sozinho segundos depois. É corrida do boot, não
        // perda de sessão. Pro usuário parece igual ("caí"); pro conserto não é,
        // e juntar as duas num número só faria o instrumento contar boot como
        // defeito de sessão.
        //
        // O juiz é o token DESTE instante: ainda vale? Então ninguém perdeu
        // sessão nenhuma. Ver `classeDaSaida`.
        const classe = classeDaSaida();
        if (tinhaSessaoRef.current && !saidaPedidaRef.current) {
          registrarQueda({
            classe,
            quando: new Date().toISOString(),
            motivo:
              consumirMotivoDaQueda() ??
              (classe === "flash_login"
                ? "painel foi pro login com token AINDA VÁLIDO — corrida do boot"
                : "sessão sumiu sem pedido de saída"),
            rota: window.location.pathname || "/",
          });
        }
        // O marco some na saída PEDIDA e na queda já contada. No flash ele FICA:
        // a sessão não morreu, e apagá-lo aqui deixaria a próxima queda de
        // verdade invisível outra vez — o instrumento se cegaria a cada boot.
        if (classe !== "flash_login") limparSessaoViva();
        saidaPedidaRef.current = false;
        tinhaSessaoRef.current = false;
        // Sessão expirou/saiu — invalida qualquer loadUserProfile em voo
        activeSessionRef.current = null;
        setUser(null);
        setLoading(false);
        // 🔑 O MESMO CORTE DA OUTRA PORTA. `flash_login` = o token ainda vale;
        // não dá pra afirmar "não há usuário" com a credencial boa na mão. Só a
        // queda CONFIRMADA resolve o boot e libera o /login.
        if (classe !== "flash_login") setBootResolvido(true);
      }
    });

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(tetoDoBoot);
    };
  }, []);

  /**
   * O PERFIL PELA BORDA, com o caminho antigo como rede de segurança.
   *
   * Esta consulta é a ÚNICA do boot que falava direto com o Supabase (EUA) —
   * todo o resto do painel passa pelo Worker da Cloudflare. E ela trava a tela
   * inteira: enquanto não volta, `loading` fica true e o chunk da rota nem é
   * pedido. Medido em produção 27/07 (M5, Chrome do dono): 2.427–9.993 ms no
   * caminho direto contra 120–360 ms em tudo que vai pelo /api — a cauda de
   * ~10 s era o "demoooora pra carregar".
   *
   * Se o /api falhar (deploy defasado, 403 de vínculo), cai no caminho antigo:
   * mesma consulta, mesma RLS. Nenhum modo de falha novo.
   */
  async function buscarPerfil(sess: Session) {
    try {
      const r = await apiFetch<{ data: PerfilRow }>("/me/perfil", sess.access_token, {
        // 🔴 A requisição do boot tem PRAZO. Sem isto ela podia ficar pendurada
        // pra sempre — e foi o que aconteceu em 28/07: `/me/perfil` PENDENTE na
        // rede, `loading` preso em true, painel em "Carregando…" e nem a aba
        // trocava de nome (o <title> ficava no "VORTEK" do index.html, porque
        // nem o Login nem o Layout chegavam a montar pra escrever nele).
        // Estourando, cai no caminho direto abaixo — que é a rede de segurança
        // que já existia, agora alcançável.
        prazoMs: PRAZO_PERFIL_BORDA_MS,
      });
      if (r?.data) return r.data;
    } catch {
      // segue pro caminho direto
    }
    const { data } = await supabase
      .from("users")
      .select("nome, role, cliente_id, is_owner")
      .eq("id", sess.user.id)
      .single();
    return data as PerfilRow | null;
  }

  function loadUserProfile(sess: Session): Promise<void> {
    // Marca esta chamada com um token único (id + fim do access_token)
    const token = sess.user.id + "|" + sess.access_token.slice(-10);
    const emVoo = perfilEmVooRef.current;
    if (emVoo?.token === token) return emVoo.promessa;

    activeSessionRef.current = token;
    const promessa = carregarPerfil(sess, token).finally(() => {
      if (perfilEmVooRef.current?.token === token) perfilEmVooRef.current = null;
    });
    perfilEmVooRef.current = { token, promessa };
    return promessa;
  }

  async function carregarPerfil(sess: Session, token: string) {
    // Blindagem: qualquer erro aqui (rede, token expirando no meio da query) NÃO
    // pode deixar loading=true pra sempre. finally garante que o painel destrava.
    //
    // 🔴 …mas `finally` só roda se a promessa TERMINAR, e era exatamente isso que
    // faltava: uma requisição pendurada não erra, não volta, e não deixa o
    // `finally` acontecer. O TETO abaixo é o que torna a blindagem verdadeira —
    // passado o prazo, o boot desiste do perfil e entra com o mínimo (o mesmo
    // caminho do `catch`, que já existia), em vez de congelar a tela.
    try {
      const data = await comPrazo(buscarPerfil(sess), TETO_PERFIL_MS);

      // Se a sessão mudou enquanto aguardávamos, ignorar resultado
      if (activeSessionRef.current !== token) return;

      if (!data) {
        setUser({ id: sess.user.id, email: sess.user.email ?? "" });
      } else {
        setUser({
          id: sess.user.id,
          email: sess.user.email ?? "",
          nome: data.nome,
          role: data.role,
          cliente_id: data.cliente_id,
          is_owner: (data as { is_owner?: boolean }).is_owner === true,
        });
      }
    } catch {
      // Perfil não carregou, mas há sessão válida — entra com o mínimo (email).
      if (activeSessionRef.current === token) setUser({ id: sess.user.id, email: sess.user.email ?? "" });
    } finally {
      // O caminho normal: o perfil resolveu (cheio ou com o mínimo do `catch`), e
      // `user` está preenchido. É a situação 1 do `bootResolvido` — a única que
      // responde "SIM, há usuário".
      if (activeSessionRef.current === token) {
        setLoading(false);
        setBootResolvido(true);
      }
    }
  }

  async function signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    // Popula sessão + perfil ANTES de retornar, pra evitar a corrida em que a
    // tela navega pro Dashboard antes do contexto ter o usuário (rota protegida
    // jogava de volta pro login → "só entra na 2ª/3ª tentativa").
    if (data.session) {
      tinhaSessaoRef.current = true;
      marcarSessaoViva();
      setSession(data.session);
      await loadUserProfile(data.session);
      // ENTREGA o que a queda anterior deixou guardado. É aqui, e não na queda,
      // porque na hora da queda não existe sessão pra falar com o servidor — e a
      // rota é autenticada de propósito (endpoint aberto de escrita em
      // `app_errors` seria porta de abuso). Best-effort: se falhar, fica pro
      // próximo login. Não damos `await` no login por causa da telemetria.
      void enviarQuedasPendentes(data.session.access_token);
    }
    return { error: null };
  }

  async function signOut() {
    // O CLIQUE EXISTIU: a saída que vem a seguir é PEDIDA, não queda. O marco sai
    // AGORA, antes do `signOut` — se o navegador for fechado no meio, o boot
    // seguinte não pode achar marco vivo e contar uma queda que não houve.
    saidaPedidaRef.current = true;
    limparSessaoViva();
    await supabase.auth.signOut();
  }

  /**
   * 🔴 A SESSÃO RENOVA ENQUANTO A PESSOA USA — 24/08.
   *
   * 👑 *"cada ação que o usuário tem, renova... nao quero que cx e ux do sistema
   * sejam deslogados"*. Medido em `app_errors`: 71 quedas em 14 dias, e a maior
   * causa é token vencido sem renovação.
   *
   * O supabase-js já agenda um relógio pra renovar antes de vencer. **O
   * navegador congela relógios de aba em segundo plano e de máquina dormindo** —
   * então o relógio não toca, o token vence e a próxima chamada leva 401.
   *
   * Aqui não se substitui aquele relógio: acrescenta-se o gatilho que faltava —
   * **o uso**. Voltou pra aba, clicou, digitou → confere o vencimento e renova
   * se estiver perto. `onAuthStateChange` (linha 245) recebe a sessão nova e
   * `getAccessToken` passa a devolver o crachá renovado, sem nada mais mudar.
   *
   * ⚠️ Isto NÃO estende a sessão de quem foi embora: sem uso, o token vence
   * normalmente. O que muda é que quem ESTÁ trabalhando não é interrompido.
   */
  const ultimaRenovacaoRef = useRef(0);
  useEffect(() => {
    if (!session) return;
    let vivo = true;

    async function renovarSePreciso() {
      if (!vivo) return;
      const agora = Date.now();
      if (!precisaRenovar(expiraEmMs(session?.expires_at), agora, ultimaRenovacaoRef.current)) return;
      // Marca ANTES de esperar a rede: sem isto, uma rajada de cliques dispara
      // várias renovações em voo e elas competem pelo mesmo refresh token —
      // que é exatamente como se corrompe uma sessão ("Invalid Refresh Token").
      ultimaRenovacaoRef.current = agora;
      try {
        const { error } = await supabase.auth.refreshSession();
        // 🔬 FALHOU? NÃO DESLOGA — mas DEIXA O MOTIVO ESCRITO.
        //
        // Renovar é uma tentativa a mais, nunca um motivo novo pra cair: quem
        // decide continua sendo o caminho normal de erro. O que muda aqui é o
        // RASTRO. Quando o supabase-js desiste de uma sessão, ele emite a saída
        // e o registro chega em `onAuthStateChange` como "sessão sumiu sem
        // pedido de saída" — 13 quedas assim em 14 dias, TODAS sem causa
        // conhecida, porque ninguém tinha marcado o motivo antes.
        //
        // Marcando aqui, a mesma queda passa a chegar com a causa junto. É o
        // instrumento sendo consertado antes da causa: não sei ainda o que
        // mata essas sessões, e é exatamente por isso que preciso que a
        // próxima fale.
        if (error) marcarMotivoDaQueda(`a renovação pelo uso falhou: ${error.message}`.slice(0, 300));
      } catch (e) {
        marcarMotivoDaQueda(
          `a renovação pelo uso nem chegou a responder: ${e instanceof Error ? e.message : String(e)}`.slice(0, 300),
        );
      }
    }

    // A montagem mora em `sessaoViva` justamente pra poder ser provada: aqui
    // dentro do React ela ficaria fora do alcance do portão.
    const desligar = ligarRenovacaoPeloUso({
      janela: window,
      documento: document,
      aoUsar: () => void renovarSePreciso(),
    });
    void renovarSePreciso(); // a montagem também conta como uso

    return () => {
      vivo = false;
      desligar();
    };
  }, [session]);

  function getAccessToken() {
    return session?.access_token ?? null;
  }

  // O papel também vai pro holder de módulo. Quem lê é o `ErroDados`, que decide
  // por papel se mostra o detalhe técnico do erro e NÃO pode depender de hook —
  // o porquê está no cabeçalho de `lib/papelAtual.ts`. Efeito em vez de linha
  // dentro de cada `setUser`: são cinco pontos que atribuem usuário, e o que
  // esquecesse um deixaria o holder mentindo em silêncio.
  useEffect(() => {
    definirPapelAtual(user?.role);
  }, [user?.role]);

  return (
    <AuthContext.Provider value={{ user, session, loading, bootResolvido, signIn, signOut, getAccessToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
